import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router';
import { Alert, Badge, Button, Card, Field, PageHeader, Select, Spinner } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { canManageAgents, type DeviceRow, type DeviceStatus, formatDateTime, type InventoryForm, platformName, reasonName, statusName, type TokenRow } from './data';

const tab = ({ isActive }: { isActive: boolean }) => `-mb-px border-b-2 px-3 py-2 text-sm ${isActive ? 'border-marca font-medium text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`;

/** Secciones del módulo Inventario. */
function InventoryNav() {
  const me = useMe();
  return (
    <nav aria-label={t('inv.secciones')} className="flex gap-1 border-b border-slate-200">
      <NavLink to="/inventario" end className={tab}>
        {t('inv.equipos')}
      </NavLink>
      {canManageAgents(me) && (
        <NavLink to="/inventario/enrolamiento" className={tab}>
          {t('inv.enrolamiento')}
        </NavLink>
      )}
    </nav>
  );
}

// --- Equipos ---

/** Equipos con el estado de su agente (especificación 6.1) y la revisión de los nuevos (A8 y A11). */
export function DevicesPage() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('buscar') ?? '');
  const [debounced, setDebounced] = useState(search.trim());
  const status = params.get('estado') ?? '';
  const deviceId = params.get('equipo') ?? '';
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const query = new URLSearchParams();
  if (status) query.set('estado', status);
  if (debounced) query.set('buscar', debounced);
  if (deviceId) query.set('equipo', deviceId);
  const devices = useQuery({
    queryKey: ['inventario', 'equipos', status, debounced, deviceId],
    queryFn: () => api<DeviceRow[]>(`/api/inventario/equipos?${query}`),
    placeholderData: (prev) => prev,
    refetchInterval: 60_000,
  });
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('equipo');
    setParams(next, { replace: true });
  };
  const pending = devices.data?.filter((d) => d.status === 'por_revisar').length ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader title={t('inv.titulo')} description={t('inv.descripcion')} />
      <InventoryNav />
      {pending > 0 && !status && <Alert tone="warning">{t('inv.hay_por_revisar', { n: pending })}</Alert>}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <Field id="inv-buscar" type="search" label={t('inv.buscar')} placeholder={t('inv.buscar_ayuda')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="w-48">
          <Select id="inv-estado" label={t('inv.estado')} value={status} onChange={(e) => update('estado', e.target.value)}>
            <option value="">{t('inv.estado_todos')}</option>
            {(['activo', 'por_revisar', 'retirado'] as DeviceStatus[]).map((s) => (
              <option key={s} value={s}>
                {statusName(s)}
              </option>
            ))}
          </Select>
        </div>
        {deviceId && (
          <Button variant="link" className="pb-2 text-sm" onClick={() => update('equipo', '')}>
            {t('inv.ver_todos')}
          </Button>
        )}
      </div>
      <Card className="p-0">
        {!devices.data ? (
          <div className="p-5">{devices.error ? <Alert>{errorMessage(devices.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>
        ) : devices.data.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">{t('inv.vacio')}</p>
        ) : (
          <ul className="divide-y divide-slate-100" aria-label={t('inv.equipos')}>
            {devices.data.map((d) => (
              <DeviceItem key={d.id} device={d} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function DeviceItem({ device: d }: { device: DeviceRow }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');

  async function act(path: string, body: object) {
    setBusy(true);
    setError(null);
    try {
      await api(path, { json: body });
      setRevoking(false);
      await qc.invalidateQueries({ queryKey: ['inventario'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const tone = d.status === 'por_revisar' ? 'amber' : d.status === 'retirado' ? 'slate' : 'blue';
  return (
    <li className={`space-y-2 px-5 py-3 ${d.status === 'por_revisar' ? 'bg-senal-suave/40' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-slate-900">
            {d.hostname ?? t('inv.sin_nombre')}
            <span className="ml-2 align-middle">
              <Badge tone={tone}>{statusName(d.status)}</Badge>
            </span>
          </p>
          <p className="text-xs text-slate-500">
            {[d.manufacturer, d.model].filter(Boolean).join(' ') || '—'}
            {d.serialNumber && (
              <>
                {' · '}
                <span className="font-mono">{d.serialNumber}</span>
              </>
            )}
            {' · '}
            {d.organization}
          </p>
          {d.reviewReasons.length > 0 && (
            <p className="mt-1 text-xs text-amber-900">
              {d.reviewReasons.map(reasonName).join(' · ')}
              {d.pendingOrganization && ` (${t('inv.pide_pasar', { org: d.pendingOrganization })})`}
            </p>
          )}
        </div>
        <div className="text-right text-xs text-slate-600">
          {d.agent ? (
            <>
              <p className={d.online ? 'font-medium text-green-800' : 'font-medium text-slate-700'}>
                {d.online ? t('inv.en_linea') : d.agent.lastHeartbeatAt ? t('inv.desconectado_desde', { fecha: formatDateTime(d.agent.lastHeartbeatAt) }) : t('inv.sin_latido')}
              </p>
              <p>
                {platformName(d.agent.platform)} · {t('inv.agente_version', { v: d.agent.version })}
              </p>
              {d.lastInventoryAt && <p>{t('inv.ultimo_inventario', { fecha: formatDateTime(d.lastInventoryAt) })}</p>}
            </>
          ) : (
            <p>{d.source === 'importacion' ? t('inv.sin_agente_importado') : t('inv.sin_agente')}</p>
          )}
        </div>
      </div>
      {error && <Alert>{error}</Alert>}
      {d.can.manage && (d.status === 'por_revisar' || d.agent) && (
        <div className="flex flex-wrap items-center gap-2">
          {d.status === 'por_revisar' && d.pendingOrganization && (
            <>
              <Button className="px-3 py-1.5" disabled={busy} onClick={() => void act(`/api/inventario/equipos/${d.id}/revision`, { accion: 'confirmar_cambio' })}>
                {t('inv.confirmar_cambio', { org: d.pendingOrganization })}
              </Button>
              <Button variant="secondary" className="px-3 py-1.5" disabled={busy} onClick={() => void act(`/api/inventario/equipos/${d.id}/revision`, { accion: 'rechazar_cambio' })}>
                {t('inv.rechazar_cambio')}
              </Button>
            </>
          )}
          {d.status === 'por_revisar' && !d.pendingOrganization && (
            <Button className="px-3 py-1.5" disabled={busy} onClick={() => void act(`/api/inventario/equipos/${d.id}/revision`, { accion: 'aprobar' })}>
              {t('inv.aprobar')}
            </Button>
          )}
          {d.agent && !revoking && (
            <Button variant="link" className="text-sm text-red-700" onClick={() => setRevoking(true)}>
              {t('inv.revocar_agente')}
            </Button>
          )}
        </div>
      )}
      {revoking && d.agent && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void act(`/api/inventario/agentes/${d.agent!.id}/revocar`, { motivo: reason.trim() });
          }}
        >
          <div className="w-80">
            <Field id={`inv-motivo-${d.id}`} label={t('inv.motivo_revocar')} required minLength={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <Button type="submit" variant="danger" disabled={busy || reason.trim().length < 3}>
            {t('inv.si_revocar')}
          </Button>
          <Button variant="secondary" onClick={() => setRevoking(false)}>
            {t('comun.cancelar')}
          </Button>
          <p className="w-full text-xs text-slate-500">{t('inv.revocar_ayuda')}</p>
        </form>
      )}
    </li>
  );
}

// --- Enrolamiento ---

/** Tokens de enrolamiento (A8): uno por organización u oleada, reutilizable, con vencimiento y tope de usos. */
export function EnrollmentPage() {
  const qc = useQueryClient();
  const form = useQuery({ queryKey: ['inventario', 'formulario'], queryFn: () => api<InventoryForm>('/api/inventario/formulario') });
  const tokens = useQuery({ queryKey: ['inventario', 'tokens'], queryFn: () => api<TokenRow[]>('/api/inventario/tokens') });
  const [organizationId, setOrganizationId] = useState('');
  const [label, setLabel] = useState('');
  const [maxUses, setMaxUses] = useState('30');
  const [created, setCreated] = useState<{ token: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const orgId = organizationId || form.data?.organizations[0]?.id || '';

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ token: string; expiresAt: string }>('/api/inventario/tokens', { json: { organizationId: orgId, label: label.trim(), maxUses: Number(maxUses) } });
      setCreated(res);
      setCopied(false);
      setLabel('');
      await qc.invalidateQueries({ queryKey: ['inventario', 'tokens'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setError(null);
    try {
      await api(`/api/inventario/tokens/${id}/revocar`, { method: 'POST' });
      await qc.invalidateQueries({ queryKey: ['inventario', 'tokens'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader title={t('inv.titulo')} description={t('inv.enrolamiento_descripcion')} />
      <InventoryNav />
      {error && <Alert>{error}</Alert>}
      <div className="grid gap-5 lg:grid-cols-[22rem_1fr]">
        <Card title={t('inv.nuevo_token')} className="self-start">
          {!form.data ? (
            <Spinner label={t('comun.cargando')} />
          ) : (
            <form onSubmit={(e) => void create(e)} className="space-y-3">
              <Select id="inv-org" label={t('inv.organizacion')} value={orgId} onChange={(e) => setOrganizationId(e.target.value)}>
                {form.data.organizations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.groupName} › {o.name}
                  </option>
                ))}
              </Select>
              <Field id="inv-etiqueta" label={t('inv.etiqueta')} placeholder={t('inv.etiqueta_ayuda')} required minLength={3} maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} />
              <Field id="inv-usos" type="number" min={1} max={1000} label={t('inv.tope_usos')} required value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
              <p className="text-xs text-slate-500">{t('inv.token_ayuda', { dias: form.data.tokenDays })}</p>
              <Button type="submit" disabled={busy || label.trim().length < 3 || !orgId}>
                {t('inv.crear_token')}
              </Button>
            </form>
          )}
        </Card>
        <div className="space-y-4">
          {created && (
            <Alert tone="warning">
              <p className="font-medium">{t('inv.token_creado')}</p>
              <p className="mt-2 break-all rounded bg-white px-3 py-2 font-mono text-xs text-slate-900">{created.token}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  className="px-3 py-1.5"
                  onClick={() => {
                    void navigator.clipboard?.writeText(created.token).then(() => setCopied(true));
                  }}
                >
                  {copied ? t('inv.copiado') : t('inv.copiar')}
                </Button>
                <Button variant="link" className="text-sm" onClick={() => setCreated(null)}>
                  {t('inv.ya_lo_guarde')}
                </Button>
              </div>
            </Alert>
          )}
          <Card className="p-0">
            {!tokens.data ? (
              <div className="p-5">{tokens.error ? <Alert>{errorMessage(tokens.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>
            ) : tokens.data.length === 0 ? (
              <p className="p-5 text-sm text-slate-500">{t('inv.sin_tokens')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs text-slate-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">{t('inv.col_token')}</th>
                      <th className="px-4 py-2 font-medium">{t('inv.col_usos')}</th>
                      <th className="px-4 py-2 font-medium">{t('inv.col_vence')}</th>
                      <th className="px-4 py-2 font-medium">{t('inv.estado')}</th>
                      <th className="px-4 py-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tokens.data.map((tk) => (
                      <tr key={tk.id}>
                        <td className="px-4 py-2.5">
                          <span className="font-medium text-slate-900">{tk.label}</span>
                          <span className="block text-xs text-slate-500">
                            {tk.organization} · <span className="font-mono">…{tk.hint}</span>
                            {tk.createdByName && ` · ${tk.createdByName}`}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-700">{t('inv.usos_de', { usos: tk.uses, tope: tk.maxUses })}</td>
                        <td className="px-4 py-2.5 text-slate-700">{formatDateTime(tk.expiresAt)}</td>
                        <td className="px-4 py-2.5">
                          <Badge tone={tk.state === 'vigente' ? 'blue' : 'slate'}>{t(`inv.token_${tk.state}` as MessageKey)}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {tk.state === 'vigente' && (
                            <Button variant="link" className="text-sm text-red-700" onClick={() => void revoke(tk.id)}>
                              {t('inv.revocar')}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
